import { Component } from '../../scripts/component.js';
import { isDesktopBreakpoint, normalizeCollectionStateKey } from '../../scripts/utils.js';

let hasCollectionGridScrolled = false;

const sizeRowClampedQuery = matchMedia('(max-width: 767px)');

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {any} [slideshow] - The slideshow component.
 * @property {any} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  #deferredDialogOpenListenerAttached = false;
  #deferredImageObserver;
  #upgradeRemainingHandler;
  #deferredUntilScrollHandler;
  #slideshowSelectHandler;
  #slideshowDragProgressHandler;
  #slideshowPointerDownHandler;
  #slideshowTouchStartHandler;

  /** When true, the next click that reaches {@link navigateToProduct} was a drag, not an intentional tap. */
  #suppressNextProductCardNavigation = false;
  /** @type {{ startX: number; startY: number; maxTravel: number } | null} */
  #pointerTravelSession = null;
  /** @type {((ev: PointerEvent) => void) | undefined} */
  #onPointerTravelMove;
  /** @type {(() => void) | undefined} */
  #onPointerTravelEnd;

  static #POINTER_DRAG_SUPPRESS_PX = 12;

  /**
   * Real click target. Delegated `on:click` uses a proxied event whose `target` is this card, so
   * `event.target.closest('button')` misses descendants (wishlist, etc.). `composedPath()[0]` is reliable.
   * @param {Event} event
   * @returns {Element | null}
   */
  static #getClickTargetElement(event) {
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const first = path[0];
    if (first instanceof Element) return first;
    const { target } = event;
    return target instanceof Element ? target : null;
  }

  #teardownPointerTravelListeners() {
    if (this.#onPointerTravelMove) {
      document.removeEventListener('pointermove', this.#onPointerTravelMove);
    }
    if (this.#onPointerTravelEnd) {
      document.removeEventListener('pointerup', this.#onPointerTravelEnd);
      document.removeEventListener('pointercancel', this.#onPointerTravelEnd);
    }
    this.#onPointerTravelMove = undefined;
    this.#onPointerTravelEnd = undefined;
    this.#pointerTravelSession = null;
  }

  #cleanupDeferredImageHandlers() {
    if (this.#upgradeRemainingHandler) {
      this.removeEventListener('pointerenter', this.#upgradeRemainingHandler);
      this.removeEventListener('focusin', this.#upgradeRemainingHandler);
    }

    if (this.#slideshowSelectHandler) {
      this.removeEventListener('slideshow:select', this.#slideshowSelectHandler);
      this.#slideshowSelectHandler = undefined;
    }

    if (this.#slideshowDragProgressHandler) {
      this.removeEventListener('slideshow:drag-progress', this.#slideshowDragProgressHandler);
      this.#slideshowDragProgressHandler = undefined;
    }

    if (this.#slideshowPointerDownHandler) {
      this.removeEventListener('pointerdown', this.#slideshowPointerDownHandler);
      this.#slideshowPointerDownHandler = undefined;
    }

    if (this.#slideshowTouchStartHandler) {
      this.removeEventListener('touchstart', this.#slideshowTouchStartHandler);
      this.#slideshowTouchStartHandler = undefined;
    }

    if (this.#deferredUntilScrollHandler) {
      window.removeEventListener('scroll', this.#deferredUntilScrollHandler);
      this.#deferredUntilScrollHandler = undefined;
    }
  }

  #handleCardPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (!event.isPrimary) return;
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
    if (!path.includes(this)) return;

    this.#teardownPointerTravelListeners();

    this.#pointerTravelSession = {
      startX: event.clientX,
      startY: event.clientY,
      maxTravel: 0,
    };

    this.#onPointerTravelMove = (ev) => {
      if (!this.#pointerTravelSession) return;
      const dx = ev.clientX - this.#pointerTravelSession.startX;
      const dy = ev.clientY - this.#pointerTravelSession.startY;
      const travel = Math.hypot(dx, dy);
      this.#pointerTravelSession.maxTravel = Math.max(this.#pointerTravelSession.maxTravel, travel);
    };

    this.#onPointerTravelEnd = () => {
      const session = this.#pointerTravelSession;
      this.#teardownPointerTravelListeners();
      if (session && session.maxTravel > ProductCard.#POINTER_DRAG_SUPPRESS_PX) {
        this.#suppressNextProductCardNavigation = true;
      }
    };

    document.addEventListener('pointermove', this.#onPointerTravelMove, { passive: true });
    document.addEventListener('pointerup', this.#onPointerTravelEnd);
    document.addEventListener('pointercancel', this.#onPointerTravelEnd);
  };

  #persistCollectionScrollForReturn() {
    const currentUrl = new URL(window.location.href);
    const normalizedStateKey = normalizeCollectionStateKey(currentUrl);
    sessionStorage.setItem('collection:return:url', currentUrl.toString());
    sessionStorage.setItem(`collection:scroll:${normalizedStateKey}`, String(window.scrollY));
  }

  #canStartImageRequests() {
    const dialogEl = this.closest('dialog-component')?.querySelector(':scope > dialog');
    if (!(dialogEl instanceof HTMLDialogElement)) return true;
    return dialogEl.open && !dialogEl.classList.contains('dialog-closing');
  }

  get productPageUrl() {
    return this.refs.productCardLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="radio"]:checked[data-variant-id]'));

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  connectedCallback() {
    super.connectedCallback();
    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    this.addEventListener('pointerdown', this.#handleCardPointerDown, { capture: true });

    this.#handleQuickAdd();
    this.showConnectedProducts();
    this.#setupDeferredImages();
    this.#markClippedSizes();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setupDeferredImages();
  }

  /** Re-run deferred image setup, e.g. after the collection grid restores a scroll position. */
  refreshDeferredImages() {
    this.#setupDeferredImages();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this.#handleCardPointerDown, { capture: true });
    this.#teardownPointerTravelListeners();
    this.#suppressNextProductCardNavigation = false;

    this.#deferredImageObserver?.disconnect();
    this.#deferredImageObserver = undefined;
    this.#cleanupDeferredImageHandlers();
  }

  #markClippedSizes() {
    if (!sizeRowClampedQuery.matches) return;

    requestAnimationFrame(() => this.#clipOverflowingSizes());
  }

  #clipOverflowingSizes() {
    const container = this.querySelector('.product-size-variant-overview--below');
    const row = container?.querySelector('.product-sizes');
    if (!(row instanceof HTMLElement)) return;

    const sizes = /** @type {HTMLElement[]} */ ([...row.querySelectorAll('a')]);
    if (!sizes.length) return;

    const more = container?.querySelector('.product-sizes__more');
    const firstRowTop = sizes[0].offsetTop;
    const clipped = sizes.filter((size) => size.offsetTop > firstRowTop);
    const moreWrapped = more instanceof HTMLElement && more.offsetParent !== null && more.offsetTop > firstRowTop;

    if (!clipped.length && !moreWrapped) return;

    for (const size of clipped) size.classList.add('size-clipped');
    if (!clipped.length) sizes[sizes.length - 1]?.classList.add('size-clipped');
    more?.classList.add('is-visible');
  }

  navigateToProduct(event) {
    const clickTarget = ProductCard.#getClickTargetElement(event);
    if (!(clickTarget instanceof Element)) return;

    if (this.#suppressNextProductCardNavigation) {
      this.#suppressNextProductCardNavigation = false;
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (clickTarget.closest('wishlist-button, quick-add-component')) {
      return;
    }

    const interactiveElement = clickTarget.closest(
      'button, input, label, select, textarea, [role="button"], [tabindex="1"], a[href]',
    );
    if (interactiveElement && interactiveElement !== this.refs.productCardLink) {
      if (interactiveElement.matches('a[href]')) {
        this.#persistCollectionScrollForReturn();
      }
      return;
    }

    const link = this.refs.productCardLink;
    if (!link?.href) return;
    const productUrl = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    this.#persistCollectionScrollForReturn();

    const targetLink = clickTarget.closest('a[href]') || clickTarget.closest('.mobile-wrapper');

    // If click did not originate from a link, perform navigation programmatically
    if (!targetLink) {
      productUrl.hash = productCardAnchor;
      this.navigateToURL(event, productUrl);
      return;
    }

    // If click was on a link, ensure the overlay link is clickable and let native nav happen
    link.classList.add('z-layer-flat');
    link.classList.remove('pointer-events-none');
  }

  navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab = event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Shows the connected products
   */
  showConnectedProducts = () => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.loadContent();
        }
      });
    });
    observer.observe(this);
  };

  #setupDeferredImages() {
    // Only defer/upgrade images on collection/search product grids.
    if (this.getAttribute('data-defer-images') !== 'true') return;

    if (isDesktopBreakpoint() && window.scrollY <= 0 && hasCollectionGridScrolled) {
      hasCollectionGridScrolled = false;
    }

    if (!this.#canStartImageRequests()) {
      const dialogComponent = this.closest('dialog-component');
      if (dialogComponent instanceof HTMLElement && !this.#deferredDialogOpenListenerAttached) {
        this.#deferredDialogOpenListenerAttached = true;
        dialogComponent.addEventListener(
          'dialog:open',
          () => {
            this.#deferredDialogOpenListenerAttached = false;
            this.#setupDeferredImages();
          },
          { once: true },
        );
      }
      return;
    }

    const hasDeferred = this.querySelector('img[data-srcset]') || this.querySelector('img[data-full-src]') || this.querySelector('img[data_full_src]');

    // If the card has been morphed/re-rendered, it can keep the "upgraded" flags
    // while receiving new deferred markup. Reset flags so we can upgrade again.
    if (hasDeferred) {
      if (this.getAttribute('data-product-card-primary-image-upgraded') === 'true') {
        this.removeAttribute('data-product-card-primary-image-upgraded');
      }
      if (this.getAttribute('data-product-card-all-images-upgraded') === 'true') {
        this.removeAttribute('data-product-card-all-images-upgraded');
      }
      this.querySelectorAll('slideshow-slide[data-product-card-slide-upgraded]').forEach((slide) => {
        slide.removeAttribute('data-product-card-slide-upgraded');
      });
    } else {
      // Nothing deferred to upgrade.
      return;
    }

    const upgradeImage = (img, { eager = false, highPriority = false } = {}) => {
      if (!(img instanceof HTMLImageElement)) return;

      if (eager) {
        img.setAttribute('loading', 'eager');
      }

      if (highPriority) {
        img.setAttribute('fetchpriority', 'high');
      }

      const deferredSrcset = img.getAttribute('data-srcset');
      const deferredSizes = img.getAttribute('data-sizes');
      const deferredDisplaySrc = img.getAttribute('data-display-src');

      if (deferredSrcset) {
        img.setAttribute('srcset', deferredSrcset);
        if (deferredSizes) img.setAttribute('sizes', deferredSizes);

        const fallbackSrc = deferredDisplaySrc || deferredSrcset.split(',')[0]?.trim().split(' ')[0] || '';
        if (fallbackSrc) img.setAttribute('src', fallbackSrc);

        img.removeAttribute('data-srcset');
        img.removeAttribute('data-sizes');
        img.removeAttribute('data-display-src');
        return;
      }

      // Shopify's image_tag can emit underscore attributes (data_full_src) from Liquid args.
      const fullSrc = img.getAttribute('data-full-src') || img.getAttribute('data_full_src');
      if (fullSrc) {
        img.setAttribute('src', fullSrc);
        img.removeAttribute('data-full-src');
        img.removeAttribute('data_full_src');
      }
    };

    const getPrimaryImage = () => {
      const primary = this.querySelector('.product-card-media--primary img');
      if (primary instanceof HTMLImageElement) return primary;

      const first = this.querySelector('img');
      if (first instanceof HTMLImageElement) return first;

      return null;
    };

    const upgradePrimary = () => {
      if (this.getAttribute('data-product-card-primary-image-upgraded')) return;

      const primaryImg = getPrimaryImage();
      if (!primaryImg) return;
      upgradeImage(primaryImg, { eager: true, highPriority: true });
      this.setAttribute('data-product-card-primary-image-upgraded', 'true');
    };

    const shouldPreloadNearViewportImages = this.getAttribute('data-preload-near-viewport-images') === 'true';
    const isDesktopViewport = isDesktopBreakpoint();

    const isInInitialDesktopRowsBudget = (() => {
      if (!isDesktopViewport) return true;

      const cardRoot = this.closest('.product-grid__item');
      if (!(cardRoot instanceof HTMLElement)) return true;

      const inBudget = cardRoot.getAttribute('data-initial-desktop-image-budget');
      if (inBudget === 'true') return true;
      if (inBudget === 'false') return false;
      return true;
    })();

    const upgradeRemaining = () => {
      if (this.getAttribute('data-product-card-all-images-upgraded')) return;
      const primaryImg = getPrimaryImage();

      this.querySelectorAll('img').forEach((img) => {
        if (!(img instanceof HTMLImageElement)) return;
        if (primaryImg && img === primaryImg) return;
        // Only upgrade images that are still deferred
        if (img.hasAttribute('data-srcset') || img.hasAttribute('data-full-src') || img.hasAttribute('data_full_src')) {
          upgradeImage(img, { eager: shouldPreloadNearViewportImages });
        }
      });

      this.setAttribute('data-product-card-all-images-upgraded', 'true');
    };

    if (this.#deferredImageObserver) {
      this.#deferredImageObserver.disconnect();
      this.#deferredImageObserver = undefined;
    }
    this.#cleanupDeferredImageHandlers();

    this.#upgradeRemainingHandler = upgradeRemaining;

    const slideshow = this.refs.slideshow || this.querySelector('slideshow-component');
    const hasSlideshow = slideshow instanceof HTMLElement;

    const getSlideshowSlides = () => {
      if (!hasSlideshow) return [];
      const slides = slideshow.slides || slideshow.refs?.slides;
      if (Array.isArray(slides)) return slides;
      return Array.from(slideshow.querySelectorAll('slideshow-slide'));
    };

    const upgradeSlideAtIndex = (index, { eager = true, highPriority = false } = {}) => {
      const slides = getSlideshowSlides();
      const slide = slides[index];
      if (!(slide instanceof Element)) return;
      if (slide.getAttribute('data-product-card-slide-upgraded') === 'true') return;

      let upgradedAnyImage = false;

      slide.querySelectorAll('img').forEach((img) => {
        if (!(img instanceof HTMLImageElement)) return;
        if (img.hasAttribute('data-srcset') || img.hasAttribute('data-full-src') || img.hasAttribute('data_full_src')) {
          upgradeImage(img, { eager, highPriority });
          upgradedAnyImage = true;
        }
      });

      if (upgradedAnyImage) {
        slide.setAttribute('data-product-card-slide-upgraded', 'true');
      }
    };

    const setupSlideshowProgressiveLoading = () => {
      if (!hasSlideshow) return false;
      const slides = getSlideshowSlides();
      if (!slides.length) return false;

      // Initial load: only first slide image per product card.
      upgradeSlideAtIndex(0, { eager: true, highPriority: true });

      this.#slideshowSelectHandler = (event) => {
        const selectedIndex = Number(event.detail?.index);
        if (Number.isNaN(selectedIndex)) return;

        // On interaction: load only the selected slide.
        upgradeSlideAtIndex(selectedIndex, { eager: true });
      };
      this.addEventListener('slideshow:select', this.#slideshowSelectHandler);

      this.#slideshowDragProgressHandler = (event) => {
        const projectedIndex = Number(event.detail?.index);
        const direction = Number(event.detail?.direction);
        if (Number.isNaN(projectedIndex) || Number.isNaN(direction) || direction === 0) return;

        upgradeSlideAtIndex(projectedIndex + direction, { eager: true });
      };
      this.addEventListener('slideshow:drag-progress', this.#slideshowDragProgressHandler);

      this.#slideshowPointerDownHandler = (event) => {
        if (isDesktopBreakpoint()) return;
        if (!event.isPrimary) return;
        if (event.pointerType === 'mouse') return;
        if (!(event.target instanceof Element)) return;
        if (!event.target.closest('slideshow-component')) return;

        const currentIndex = Number(slideshow.current);
        if (Number.isNaN(currentIndex)) return;

        upgradeSlideAtIndex(currentIndex + 1, { eager: true, highPriority: true });
      };
      this.addEventListener('pointerdown', this.#slideshowPointerDownHandler);

      this.#slideshowTouchStartHandler = (event) => {
        if (isDesktopBreakpoint()) return;
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (!target.closest('slideshow-component')) return;

        const currentIndex = Number(slideshow.current);
        if (Number.isNaN(currentIndex)) return;

        upgradeSlideAtIndex(currentIndex + 1, { eager: true, highPriority: true });
      };
      this.addEventListener('touchstart', this.#slideshowTouchStartHandler, { passive: true });

      return true;
    };

    const handleVisibleCard = () => {
      upgradePrimary();
      if (setupSlideshowProgressiveLoading()) return;

      if (shouldPreloadNearViewportImages) {
        upgradeRemaining();
      } else if (this.#upgradeRemainingHandler) {
        this.addEventListener('pointerenter', this.#upgradeRemainingHandler, { once: true });
        this.addEventListener('focusin', this.#upgradeRemainingHandler, { once: true });
      }
    };

    if (isDesktopViewport && !isInInitialDesktopRowsBudget && !hasCollectionGridScrolled) {
      const unlockScrollStartY = window.scrollY;

      this.#deferredUntilScrollHandler = () => {
        if (Math.abs(window.scrollY - unlockScrollStartY) < 24) return;

        hasCollectionGridScrolled = true;
        if (this.#deferredUntilScrollHandler) {
          window.removeEventListener('scroll', this.#deferredUntilScrollHandler);
          this.#deferredUntilScrollHandler = undefined;
        }
        this.#setupDeferredImages();
      };

      window.addEventListener('scroll', this.#deferredUntilScrollHandler, { passive: true });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleVisibleCard();
            observer.disconnect();
            if (this.#deferredImageObserver === observer) {
              this.#deferredImageObserver = undefined;
            }
          }
        });
      },
      {
        rootMargin: shouldPreloadNearViewportImages ? '100px 0px' : '0px 0px',
        threshold: 0.01,
      },
    );

    this.#deferredImageObserver = observer;

    observer.observe(this);
  }

  /**
   * Loads the content
   */
  loadContent = () => {
    if (this.getAttribute('data-cards-connected-products-loaded')) return;
    const template = this.querySelector('template');
    if (!template) return;
    const content = template.content.firstElementChild?.cloneNode(true);
    if (!content) return;
    this.setAttribute('data-cards-connected-products-loaded', 'true');
    template.after(content);
  };
}

// When the collection grid hydrates from sessionStorage it restores a deep scroll position,
// so the cards now in view are below the "initial desktop rows" budget and would otherwise sit
// in the wait-for-scroll branch with un-upgraded images until the user nudges the page. A restored
// non-zero scroll means the grid has already moved past the fold — flag it and re-run setup so the
// IntersectionObserver path upgrades the visible cards immediately.
// rAF: the grid dispatches this synchronously right before its own rAF scrollTo, so wait a frame for scrollY.
window.addEventListener('collection:grid:hydrated', () => {
  requestAnimationFrame(() => {
    if (window.scrollY <= 0) return;
    hasCollectionGridScrolled = true;
    document.querySelectorAll('product-card, product-card-small').forEach((card) => {
      if (card instanceof ProductCard) card.refreshDeferredImages();
    });
  });
});

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

export class ProductCardSmall extends ProductCard {
}

if (!customElements.get('product-card-small')) {
  customElements.define('product-card-small', ProductCardSmall);
}
